/**
 * A seller's constellation: the little cluster of stars that sits behind their
 * name on a product page and on the search page's seller cards, drawn by
 * src/components/ui/seller-constellation.tsx. This half is pure (no React, no
 * DOM) so the node test runner can pin it (src/lib/seller-constellation.test.ts).
 *
 * SEEDED BY THE SELLER, NOT RANDOM. Everything comes from mulberry32 streams
 * hashed from the seller's uid, so a seller keeps the same constellation on
 * every surface and every visit, and the server and the client could never
 * disagree about it. Shape and motion draw from separate streams, so retuning
 * a twinkle range does not rearrange every seller's stars.
 *
 * GEOMETRY. One fixed box, BOX_W x BOX_H CSS px, vertically centred on the
 * name's line box and starting just left of it. The name's line sits in
 * TEXT_BAND; no star is placed inside it, so a dot can never land on a glyph
 * and read as punctuation. Stars go in the strips above and below instead, at
 * least one in each, so the lines between them cross behind the name. A fixed
 * box rather than one stretched to the name, because stretching distorts the
 * shape, and the same seller would then look different on the two surfaces.
 *
 * LINES are the Euclidean minimum spanning tree of the stars (which never
 * crosses itself), plus, now and then, one short closing edge that crosses
 * nothing, the way drawn constellations loop back on themselves. They are
 * invisible at rest: a slow shine sweeps across them left to right every half
 * minute or so and fades out again. The stars twinkle on their own clocks, and
 * the whole figure drifts a few px and rocks a few degrees in place.
 *
 * DUST. A few fainter, unconnected specks cluster near the linked stars, so the
 * figure reads as part of a star cluster rather than a diagram.
 */

import { deriveStream, type Rng } from './seeded-random';

/* ----------------------------------------------------------------- the box */

export const BOX_W = 128;
export const BOX_H = 48;
/**
 * The name's line in box coordinates. 24px covers the search card's 16px/24px
 * name and the product byline's 14px/20px one, both centred on BOX_H / 2.
 */
export const TEXT_BAND_TOP = 12;
export const TEXT_BAND_BOTTOM = 36;
/** Clear space between a star's disc and the band or the box edge. */
const STAR_CLEARANCE = 0.6;

/* --------------------------------------------------------------- the stars */

export const MIN_STARS = 4;
export const MAX_STARS = 7;
export const MIN_STAR_SPACING = 10;
export const STAR_R_MIN = 0.6;
export const STAR_R_MAX = 1.2;
/** The one brightest star every constellation has. */
export const ANCHOR_STAR_R = 1.4;
/** The figure must span at least this much of the box, or it reads as a smudge. */
export const MIN_SPREAD_X = 48;

export const MIN_DUST = 2;
export const MAX_DUST = 5;
export const DUST_R_MIN = 0.3;
export const DUST_R_MAX = 0.5;
/** How far a speck of dust may sit from the linked star it clusters around. */
const DUST_REACH_X = 14;
const DUST_REACH_Y = 6;
export const MIN_DUST_SPACING = 4;

export const TWINKLE_MIN_MS = 3000;
export const TWINKLE_MAX_MS = 7000;
/**
 * Opacity ranges. Kept low on purpose: the text in front is near-white on a
 * near-black page, and the constellation is background, never a competitor.
 */
export const STAR_LO_MIN = 0.15;
export const STAR_LO_MAX = 0.3;
export const STAR_HI_MIN = 0.45;
export const STAR_HI_MAX = 0.75;
export const DUST_LO_MIN = 0.08;
export const DUST_LO_MAX = 0.16;
export const DUST_HI_MIN = 0.25;
export const DUST_HI_MAX = 0.4;

/* --------------------------------------------------------------- the lines */

export const LINE_WIDTH = 0.6;
/** Gap between a line's end and the edge of its star's disc. */
export const LINE_GAP = 0.9;
export const CLOSING_EDGE_RATE = 0.25;
/** A closing edge longer than this spans the figure rather than closing it. */
export const MAX_CLOSING_EDGE = 64;
/** The shine cycle: invisible for most of it, a slow reveal near the end. */
export const SHINE_MIN_MS = 24_000;
export const SHINE_MAX_MS = 48_000;
/** How long the shine takes to travel from the box's left edge to its right. */
export const SHINE_SWEEP_MS = 2500;
export const SHINE_PEAK_MIN = 0.32;
export const SHINE_PEAK_MAX = 0.42;

/* --------------------------------------------------------------- the drift */

export const DRIFT_MIN_MS = 50_000;
export const DRIFT_MAX_MS = 90_000;
export const DRIFT_MAX_PX = 3;
export const DRIFT_MAX_DEG = 4;

/* --------------------------------------------------------------- the types */

export type ConstellationStar = {
  x: number;
  y: number;
  r: number;
  /** Twinkle opacity range; the star rests at `lo` when nothing animates. */
  lo: number;
  hi: number;
  twinkleMs: number;
  /** Negative, so every star starts part way through its cycle. */
  twinkleDelayMs: number;
};

export type ConstellationLine = {
  /** Indices into `stars`, a < b. */
  a: number;
  b: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** The shared shine phase plus this line's place in the left-to-right sweep. */
  shineDelayMs: number;
};

/** Two poses the figure eases between (and back to rest), all relative to rest. */
export type ConstellationDrift = {
  durationMs: number;
  delayMs: number;
  dx: number;
  dy: number;
  rot: number;
  dx2: number;
  dy2: number;
  rot2: number;
};

export type ConstellationGeometry = {
  stars: ConstellationStar[];
  dust: ConstellationStar[];
  lines: ConstellationLine[];
  /** True when `lines` carries one edge beyond the spanning tree. */
  closed: boolean;
  shineMs: number;
  shinePeak: number;
  drift: ConstellationDrift;
};

/* ----------------------------------------------------------------- helpers */

type Pt = { x: number; y: number; r: number };

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
/**
 * Two decimals: plenty at 1:1 scale, and it keeps the markup short. Applied
 * as each value is drawn, so every spacing and band rule holds for the exact
 * numbers that reach the markup.
 */
const round2 = (v: number) => Math.round(v * 100) / 100;
const dist = (p: { x: number; y: number }, q: { x: number; y: number }) => Math.hypot(p.x - q.x, p.y - q.y);
const between = (rng: Rng, lo: number, hi: number) => lerp(lo, hi, rng());
const intBetween = (rng: Rng, lo: number, hi: number) => lo + Math.floor(rng() * (hi - lo + 1));
const sign = (rng: Rng) => (rng() < 0.5 ? -1 : 1);

/** The vertical ranges a star of radius r may sit in: the strip above the band, and the one below. */
function stripY(r: number): { top: [number, number]; bottom: [number, number] } {
  const pad = r + STAR_CLEARANCE;
  return {
    top: [pad, TEXT_BAND_TOP - pad],
    bottom: [TEXT_BAND_BOTTOM + pad, BOX_H - pad],
  };
}

function xRange(r: number): [number, number] {
  const pad = r + STAR_CLEARANCE;
  return [pad, BOX_W - pad];
}

const isAbove = (p: { y: number }) => p.y < TEXT_BAND_TOP;

/** Proper intersection of segments pq and rs (touching at a shared endpoint does not count). */
function segmentsCross(p: Pt, q: Pt, r: Pt, s: Pt): boolean {
  const orient = (a: Pt, b: Pt, c: Pt) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  const d1 = orient(r, s, p);
  const d2 = orient(r, s, q);
  const d3 = orient(p, q, r);
  const d4 = orient(p, q, s);
  return d1 * d2 < 0 && d3 * d4 < 0;
}

/** Distance from p to the segment ab. */
function distToSegment(p: Pt, a: Pt, b: Pt): number {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const len2 = vx * vx + vy * vy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * vx + (p.y - a.y) * vy) / len2));
  return Math.hypot(p.x - (a.x + t * vx), p.y - (a.y + t * vy));
}

/* ------------------------------------------------------------------ layout */

/**
 * Rejection-sample `n` stars into the two strips. Null when this attempt did
 * not produce a usable figure (too crowded, one strip empty, or too narrow);
 * the caller simply tries again from where the stream left off.
 */
function tryLayout(rng: Rng, n: number, radii: number[]): Pt[] | null {
  const stars: Pt[] = [];
  for (let tries = 0; stars.length < n && tries < 200; tries++) {
    const r = radii[stars.length];
    const strips = stripY(r);
    const [y0, y1] = rng() < 0.5 ? strips.top : strips.bottom;
    const [x0, x1] = xRange(r);
    const p = { x: round2(between(rng, x0, x1)), y: round2(between(rng, y0, y1)), r };
    if (stars.some((q) => dist(p, q) < MIN_STAR_SPACING)) continue;
    stars.push(p);
  }
  if (stars.length < n) return null;
  if (!stars.some(isAbove) || stars.every(isAbove)) return null;
  const xs = stars.map((s) => s.x);
  if (Math.max(...xs) - Math.min(...xs) < MIN_SPREAD_X) return null;
  return stars;
}

/**
 * A layout that always satisfies every rule, for the (practically unreachable)
 * case where sampling keeps failing: evenly spaced across the box, alternating
 * above and below the name. Still seeded through the strips' y values.
 */
function fallbackLayout(rng: Rng, n: number, radii: number[]): Pt[] {
  return radii.slice(0, n).map((r, i) => {
    const [x0, x1] = xRange(ANCHOR_STAR_R);
    const strips = stripY(r);
    const [y0, y1] = i % 2 === 0 ? strips.top : strips.bottom;
    return { x: round2(lerp(x0, x1, i / (n - 1))), y: round2(between(rng, y0, y1)), r };
  });
}

/** Prim's algorithm over the complete graph; n is at most MAX_STARS, so O(n^3) is nothing. */
function spanningTree(stars: Pt[]): Array<[number, number]> {
  const inTree = new Set<number>([0]);
  const edges: Array<[number, number]> = [];
  while (inTree.size < stars.length) {
    let best: [number, number] | null = null;
    let bestLen = Infinity;
    for (const i of inTree) {
      for (let j = 0; j < stars.length; j++) {
        if (inTree.has(j)) continue;
        const len = dist(stars[i], stars[j]);
        if (len < bestLen) {
          bestLen = len;
          best = [i, j];
        }
      }
    }
    // Every star is reachable in a complete graph, so best is always set.
    const [i, j] = best!;
    edges.push(i < j ? [i, j] : [j, i]);
    inTree.add(j);
  }
  return edges;
}

/** The shortest non-tree edge within MAX_CLOSING_EDGE that crosses nothing and grazes no star, if any. */
function closingEdge(stars: Pt[], tree: Array<[number, number]>): [number, number] | null {
  const taken = new Set(tree.map(([a, b]) => `${a}:${b}`));
  let best: [number, number] | null = null;
  let bestLen = Infinity;
  for (let a = 0; a < stars.length; a++) {
    for (let b = a + 1; b < stars.length; b++) {
      if (taken.has(`${a}:${b}`)) continue;
      const len = dist(stars[a], stars[b]);
      if (len > MAX_CLOSING_EDGE || len >= bestLen) continue;
      const crosses = tree.some(([c, d]) => segmentsCross(stars[a], stars[b], stars[c], stars[d]));
      if (crosses) continue;
      // A tree edge never passes close to a third star (it would be the
      // longest side of that triangle, which a spanning tree drops), but a
      // closing edge can run straight through one.
      const grazes = stars.some((s, k) => k !== a && k !== b && distToSegment(s, stars[a], stars[b]) < s.r + LINE_GAP + 1);
      if (grazes) continue;
      best = [a, b];
      bestLen = len;
    }
  }
  return best;
}

/** Specks clustered near the linked stars, in the same strips, never crowding anything. */
function placeDust(rng: Rng, stars: Pt[]): Pt[] {
  const want = intBetween(rng, MIN_DUST, MAX_DUST);
  const dust: Pt[] = [];
  for (let tries = 0; dust.length < want && tries < 60; tries++) {
    const host = stars[Math.floor(rng() * stars.length)];
    const r = round2(between(rng, DUST_R_MIN, DUST_R_MAX));
    const strips = stripY(r);
    const [y0, y1] = isAbove(host) ? strips.top : strips.bottom;
    const [x0, x1] = xRange(r);
    const x = round2(host.x + between(rng, -DUST_REACH_X, DUST_REACH_X));
    const y = round2(host.y + between(rng, -DUST_REACH_Y, DUST_REACH_Y));
    if (x < x0 || x > x1 || y < y0 || y > y1) continue;
    const p = { x, y, r };
    if (stars.some((q) => dist(p, q) < MIN_DUST_SPACING + q.r)) continue;
    if (dust.some((q) => dist(p, q) < MIN_DUST_SPACING)) continue;
    dust.push(p);
  }
  return dust;
}

/* -------------------------------------------------------------------- main */

/**
 * The constellation for one seller, or null when there is nothing to seed it
 * with (a missing or blank id), in which case nothing is drawn.
 */
export function constellationFor(seed: string | null | undefined): ConstellationGeometry | null {
  if (typeof seed !== 'string' || seed.trim() === '') return null;

  const shape = deriveStream(0, `seller-constellation:shape:${seed}`);
  const motion = deriveStream(0, `seller-constellation:motion:${seed}`);

  const n = intBetween(shape, MIN_STARS, MAX_STARS);
  const anchor = Math.floor(shape() * n);
  const radii = Array.from({ length: n }, (_, i) => (i === anchor ? ANCHOR_STAR_R : round2(between(shape, STAR_R_MIN, STAR_R_MAX))));

  let sampled: Pt[] | null = null;
  for (let attempt = 0; attempt < 40 && !sampled; attempt++) sampled = tryLayout(shape, n, radii);
  const placed = sampled ?? fallbackLayout(shape, n, radii);

  const tree = spanningTree(placed);
  const extra = shape() < CLOSING_EDGE_RATE ? closingEdge(placed, tree) : null;
  const edges = extra ? [...tree, extra] : tree;

  const dustPts = placeDust(shape, placed);

  const twinkle = (p: Pt, lo: [number, number], hi: [number, number]): ConstellationStar => {
    const twinkleMs = Math.round(between(motion, TWINKLE_MIN_MS, TWINKLE_MAX_MS));
    return {
      ...p,
      lo: round2(between(motion, lo[0], lo[1])),
      hi: round2(between(motion, hi[0], hi[1])),
      twinkleMs,
      twinkleDelayMs: -Math.round(motion() * twinkleMs),
    };
  };
  const stars = placed.map((p) => twinkle(p, [STAR_LO_MIN, STAR_LO_MAX], [STAR_HI_MIN, STAR_HI_MAX]));
  const dust = dustPts.map((p) => twinkle(p, [DUST_LO_MIN, DUST_LO_MAX], [DUST_HI_MIN, DUST_HI_MAX]));

  // One shine phase for the whole figure, negative so sellers side by side on
  // the search page do not all light up together; each line then waits its
  // turn by where its midpoint sits, which is what makes the reveal sweep.
  const shineMs = Math.round(between(motion, SHINE_MIN_MS, SHINE_MAX_MS));
  const shinePhaseMs = -Math.round(motion() * shineMs);
  const shinePeak = round2(between(motion, SHINE_PEAK_MIN, SHINE_PEAK_MAX));

  const lines = edges.map(([a, b]): ConstellationLine => {
    const p = placed[a];
    const q = placed[b];
    const len = dist(p, q);
    const ux = (q.x - p.x) / len;
    const uy = (q.y - p.y) / len;
    const trimA = p.r + LINE_GAP;
    const trimB = q.r + LINE_GAP;
    const midX = (p.x + q.x) / 2;
    return {
      a,
      b,
      x1: round2(p.x + ux * trimA),
      y1: round2(p.y + uy * trimA),
      x2: round2(q.x - ux * trimB),
      y2: round2(q.y - uy * trimB),
      shineDelayMs: shinePhaseMs + Math.round((midX / BOX_W) * SHINE_SWEEP_MS),
    };
  });

  // The second pose rocks back the other way, so the figure turns a little
  // each side of rest rather than leaning one way and back.
  const durationMs = Math.round(between(motion, DRIFT_MIN_MS, DRIFT_MAX_MS));
  const turn = sign(motion);
  const drift: ConstellationDrift = {
    durationMs,
    delayMs: -Math.round(motion() * durationMs),
    dx: round2(sign(motion) * between(motion, 1, DRIFT_MAX_PX)),
    dy: round2(sign(motion) * between(motion, 0.5, DRIFT_MAX_PX)),
    rot: round2(turn * between(motion, 1.5, DRIFT_MAX_DEG)),
    dx2: round2(sign(motion) * between(motion, 0.5, DRIFT_MAX_PX)),
    dy2: round2(sign(motion) * between(motion, 0.5, DRIFT_MAX_PX)),
    rot2: round2(-turn * between(motion, 1, DRIFT_MAX_DEG)),
  };

  return { stars, dust, lines, closed: extra !== null, shineMs, shinePeak, drift };
}
